/**
 * Function Rules generator.
 *
 * A rule y = m x + b applied to inputs. The student finds an output for a
 * given input, or the missing output in a small input/output table. Values are
 * computed by exact evaluation of a LinearExpr, so the answer is correct by
 * construction and re-verified.
 *
 * Families:
 *   R1: apply the rule to one input          -> number (output)
 *   R2: find the missing output in a table    -> number
 *   R3: find the input that gives an output    -> number (inverse)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { LinearExpr, LinearEquation } from "../engine/linear";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";
import { getTopic } from "../curriculum/curriculum";

const TOPIC = "function-rules" as const;
const VERSION = "1.0.0";

type Family = "R1" | "R2" | "R3";

interface Cfg {
  readonly families: readonly Family[];
  readonly mMax: number;
  readonly bMax: number;
  readonly inMax: number;
  readonly pNeg: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["R1"], mMax: 4, bMax: 8, inMax: 8, pNeg: 0.1 },
  standard: { families: ["R1", "R2"], mMax: 6, bMax: 10, inMax: 10, pNeg: 0.35 },
  hard: { families: ["R1", "R2", "R3"], mMax: 8, bMax: 12, inMax: 12, pNeg: 0.5 },
  challenge: { families: ["R2", "R3"], mMax: 10, bMax: 12, inMax: 12, pNeg: 0.55 },
};

function ruleLatex(m: Rational, b: Rational): string {
  const mPart = m.equals(rat(1)) ? "x" : m.equals(rat(-1)) ? "-x" : `${m.toLatex()}x`;
  if (b.isZero()) return `y = ${mPart}`;
  return `y = ${mPart} ${b.isNegative() ? `- ${b.neg().toLatex()}` : `+ ${b.toLatex()}`}`;
}

/** Wrap negatives in parentheses for substitution clarity, e.g. "(-3)". */
function paren(v: Rational): string {
  return v.isNegative() ? `(${v.toLatex()})` : v.toLatex();
}

/** A signed continuation term, e.g. "+ 5" or "- 5". */
function signedTerm(v: Rational): string {
  return v.isNegative() ? `- ${v.neg().toLatex()}` : `+ ${v.toLatex()}`;
}

/** The "m x" part with 1/-1 handling. */
function mVarLatex(m: Rational): string {
  return m.equals(rat(1)) ? "x" : m.equals(rat(-1)) ? "-x" : `${m.toLatex()}x`;
}

/** Steps for evaluating y = m x + b at a given input. */
function evalSteps(
  m: Rational,
  b: Rational,
  x: Rational,
  result: Rational,
  lhs: string,
): { description: string; latex: string }[] {
  const product = m.mul(x);
  const steps = [
    {
      description: `Substitute ${x.toLatex()} for x.`,
      latex: `${lhs} = ${m.toLatex()}(${paren(x)})${b.isZero() ? "" : " " + signedTerm(b)}`,
    },
  ];
  if (b.isZero()) {
    steps.push({ description: "Multiply.", latex: `${lhs} = ${result.toLatex()}` });
  } else {
    steps.push({
      description: "Multiply first (order of operations).",
      latex: `${lhs} = ${product.toLatex()} ${signedTerm(b)}`,
    });
    steps.push({
      description: `Then ${b.isNegative() ? "subtract" : "add"}.`,
      latex: `${lhs} = ${result.toLatex()}`,
    });
  }
  return steps;
}

/** Steps for solving m x + b = output for x. */
function solveSteps(
  m: Rational,
  b: Rational,
  output: Rational,
  x: Rational,
): { description: string; latex: string }[] {
  const afterSub = output.sub(b);
  const steps = [
    {
      description: "Set the rule equal to the output.",
      latex: `${mVarLatex(m)} ${b.isZero() ? "" : signedTerm(b)} = ${output.toLatex()}`,
    },
  ];
  if (!b.isZero()) {
    steps.push({
      description: `Undo the ${b.isNegative() ? "subtraction" : "addition"}: ${b.isNegative() ? "add" : "subtract"} ${b.abs().toLatex()} on both sides.`,
      latex: `${mVarLatex(m)} = ${afterSub.toLatex()}`,
    });
  }
  steps.push({
    description: `Divide both sides by ${m.toLatex()}.`,
    latex: `x = \\frac{${afterSub.toLatex()}}{${m.toLatex()}} = ${x.toLatex()}`,
  });
  return steps;
}

export class FunctionRulesGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = ["easy", "standard", "hard", "challenge"];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);
      const m = rat(rng.int(2, cfg.mMax) * rng.sign(cfg.pNeg));
      const b = rat(rng.int(1, cfg.bMax) * rng.sign(cfg.pNeg));
      const rule = new LinearExpr(m, b);

      let promptText: string;
      let promptLatex: string;
      let answer: Rational;
      let steps: { description: string; latex: string }[];

      if (family === "R2") {
        // Table with 3 rows; one output missing.
        const inputs = [
          rng.intNonZero(-cfg.inMax, cfg.inMax),
          rng.intNonZero(-cfg.inMax, cfg.inMax),
          rng.intNonZero(-cfg.inMax, cfg.inMax),
        ];
        const missingIdx = rng.int(0, 2);
        const rows = inputs.map((x, i) => {
          const y = rule.evaluate(rat(x));
          return { x, y, hidden: i === missingIdx };
        });
        answer = rows[missingIdx]!.y;
        const xm = rat(inputs[missingIdx]!);
        const tableLatex =
          "\\begin{array}{c|c} x & y \\\\ \\hline " +
          rows.map((r) => `${r.x} & ${r.hidden ? "?" : r.y.toLatex()}`).join(" \\\\ ") +
          " \\end{array}";
        promptText = `${ruleLatex(m, b)}. Find the missing y for x = ${inputs[missingIdx]}.`;
        promptLatex = `${ruleLatex(m, b)}\\quad ${tableLatex}`;
        steps = evalSteps(m, b, xm, answer, "y");
      } else if (family === "R3") {
        const x = rat(rng.intNonZero(-cfg.inMax, cfg.inMax));
        const output = rule.evaluate(x);
        answer = x;
        promptText = `${ruleLatex(m, b)}. What input x gives y = ${output.toString()}?`;
        promptLatex = `${ruleLatex(m, b)}\\text{. What } x \\text{ gives } y = ${output.toLatex()}?`;
        const eq = new LinearEquation(rule, LinearExpr.constantExpr(output));
        if (!eq.solve().value?.equals(x)) continue;
        steps = solveSteps(m, b, output, x);
      } else {
        const x = rat(rng.intNonZero(-cfg.inMax, cfg.inMax));
        answer = rule.evaluate(x);
        promptText = `${ruleLatex(m, b)}. Find y when x = ${x.toString()}.`;
        promptLatex = `${ruleLatex(m, b)}\\text{. Find } y \\text{ when } x = ${x.toLatex()}.`;
        steps = evalSteps(m, b, x, answer, "y");
      }

      if (answer.isZero()) continue;

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: promptText, latex: promptLatex },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps,
        hints: [
          { level: 1, text: "Put the input in for x, then compute y." },
          { level: 2, text: "Multiply first, then add or subtract the constant." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const functionRules = new FunctionRulesGenerator();
